import { useAppContext, useConfig } from '@seek/libs-shared';
import { getHostName } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import { useEffect } from 'react';
import translations from './.vocab';

import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { useHubble } from '@seek/hubble';
import type { AppConfig } from '../../../shared/config';
import { useAuth } from '../../../shared/providers/authProvider';
import { trackLinkEvents } from '../../../shared/tracking/candidateApp/trackLinkEvents';
import { trackViewEvents } from '../../../shared/tracking/candidateApp/trackViewEvents';
import { MetaTag } from '../../components/MetaTag/MetaTag';
import { getMetaData } from '../../components/MetaTag/meta';
import { CompaniesList } from '../../components/companiesList/CompaniesList';
import { ExploreCompanies } from '../../components/exploreCompanies/ExploreCompanies';
import { HomePageBanner } from '../../components/homePageBanner/HomePageBanner';
import { ValuePropMarketing } from '../../components/valuePropMarketing/ValuePropMarketing';
import { MainLayout } from '../../layouts/mainLayout/MainLayout';
import type { HomePageModel } from './homePageModel';

export function HomePage({ model }: { model: HomePageModel }) {
  const config = useConfig<AppConfig>();
  const { userId } = useAuth();
  const { ...context } = useAppContext();
  const { authenticationStatus } = useAuth();
  const { t } = useTranslations(translations);
  const hubble = useHubble();

  useEffect(() => {
    hubble.updateOptions({ pageName: 'explore companies' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackTopCompanyProfileLinkClicked = ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => {
    trackLinkEvents.trackTopCompanyProfileLinkClicked({
      config,
      context,
      linkedCompanyId,
    });
  };

  useEffect(() => {
    if (authenticationStatus !== AUTHENTICATION_TYPES.AUTH_PENDING)
      trackViewEvents.homePage({
        config,
        context: { ...context },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationStatus]);

  const { siteName: site, environment, absoluteUrl, consolidated } = config;

  useEffect(() => {
    scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <MainLayout showMarginTopAndBottom={false}>
      <MetaTag
        {...getMetaData(config, {
          brandUrl: getHostName({
            site,
            staging: environment === 'staging' || environment === 'development',
            ...(consolidated ? { alternative: 'subDomainRegion' } : {}),
          }),
          absoluteUrl,
        })}
      />
      <Stack space={['large', 'xxlarge']}>
        <HomePageBanner />
        <Stack space="xxlarge">
          <ExploreCompanies userId={userId} />
          <ValuePropMarketing />
        </Stack>
        <CompaniesList
          title={t('Top companies')}
          allCompanies={model.topCompanies}
          trackCompanyProfileLinkClicked={trackTopCompanyProfileLinkClicked}
        />
      </Stack>
    </MainLayout>
  );
}
